import { AllocationModel, ExpiryStatus, Currency } from "../../../../Generated/Commodity-Management-Api";
import { Chip, ClickAwayListener, IconButton, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { GridRow, GroupingOption } from "../../../../Types";
import { CellClassParams, IAggFuncParams, ICellRendererParams, IFilterDef, IFilterParams, ValueFormatterFunc, ValueFormatterParams } from 'ag-grid-community';
import dayjs from 'dayjs';
import { StoneXNumber } from '../../../StoneXMui';
import { useMemo, useRef, useState, useEffect } from 'react';
import { MoreVert } from '@mui/icons-material';

export interface AllocationRow extends GridRow, Partial<AllocationModel> {

}
export interface AllocationReportConfiguration {
    columns: {
        available: AllocationReportColumn[];
        active: AllocationReportColumn[];
    };
};

export interface AllocationReportUserOverride {
    columns?: {
        active?: AllocationReportColumn[];
    };
}

export interface AllocationReportRow extends GridRow, Partial<AllocationModel> { }

export type AllocationReportColumn =  'Allocation' | 'Commodity' | 'Counterparty' | 'Location' | 'Portfolio' | 'ContractMonth' | 'Volume' | 'TradePrice' | 'PositionType' | 'SettlementType' | 'Actions';
interface AllocationColumnConfiguration {
    colId: string;
    headerName?: string; // This is what appears on the table header
    columnName?: string; // This is a unique column name to distinguish from other versions of the same column
    parentHeader?: string;
    field?: string;
    width?: number;
    maxWidth?: number;
    minWidth?: number;
    flex?: number;
    sortable?: boolean;
    resizable?: boolean;
    suppressAutoSize?: boolean;
    hide?: boolean;
    filter?: string;
    cellClass?: string | string[] | ((params: CellClassParams<AllocationReportRow, any>) => string | string[] | undefined);
    aggFunc?: string | ((params: IAggFuncParams<AllocationReportRow, any>) => any) | null | undefined;
    valueFormatter?: (params: ValueFormatterParams<AllocationReportRow>) => string | ValueFormatterFunc<AllocationReportRow, any>;
    cellRenderer?: (params: ICellRendererParams<AllocationReportRow>) => any;
    filterParams?: any;
}

export const AllocationColumns: { [key in AllocationReportColumn]: AllocationColumnConfiguration } = {
    
    Allocation: {
        colId: 'Allocation',
        headerName: 'Allocation',
        field: 'allocationName',
        filter: 'agSetColumnFilter',
        minWidth: 125,
        flex: 1,
        sortable: true,
    },
    Commodity: {
        colId: 'Commodity',
        headerName: 'Commodity',
        field: 'commodity.commodityName',
        filter: 'agSetColumnFilter',
        minWidth: 125,
        flex: 1,
        sortable: true,
    },
    Counterparty: {
        colId: 'Counterparty',
        headerName: 'Counterparty',
        field: 'counterparty.counterpartyName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 125,
        flex: 1,
    },
    Location: {
        colId: 'Location',
        headerName: 'Location',
        field: 'location.locationName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 125,
        flex: 1,
    },
    Portfolio: {
        colId: 'Portfolio',
        headerName: 'Portfolio',
        field: 'portfolio.portfolioName',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 125,
        flex: 1,
    },
    ContractMonth: {
        colId: 'Contract Month',
        headerName: 'Contract Month',
        field: 'monthlyVolume',
        filter: 'agDateColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
        cellRenderer: (params: ICellRendererParams<AllocationReportRow>) => {
            const monthlyVolume = typeof params.value === 'string' ? JSON.parse(params.value) : params.value;
            const months = Object.keys(monthlyVolume).sort();
            if (months.length === 0) return '';
            const first = months[0];
            const last = months[months.length - 1];
            // Format as "MMM YYYY"
            const format = (ym: string) => {
                const [year, month] = ym.split('-');
                return dayjs(`${year}-${month}-01`).format('MMM YYYY');
            };
            return `${format(first)} - ${format(last)}`;
        }
    },
    Volume: {
        colId: 'Volume',
        headerName: 'Volume',
        field: 'monthlyVolume',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<AllocationReportRow>) => {
            const monthlyVolume = typeof params.value === 'string' ? JSON.parse(params.value) : params.value;
            if (!monthlyVolume || typeof monthlyVolume !== 'object') return '';
            const sum = Object.values(monthlyVolume).reduce((acc: number, val) => acc + (typeof val === 'number' ? val : 0), 0);
            return <StoneXNumber number={sum}></StoneXNumber>;
        },
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    TradePrice: {
        colId: 'Trade Price',
        headerName: 'Trade Price',
        field: 'price',
        filter: 'agNumberColumnFilter',
        cellRenderer: (params: ICellRendererParams<AllocationReportRow>) => params.node.allChildrenCount ?? 0 > 0 ? null : <StoneXNumber number={params.value} currency={Currency.Usd} decimals={params.data?.commodity?.displayPrecision}></StoneXNumber>,
        minWidth: 100,
        flex: 1,
    },
    PositionType: {
        colId: 'Position Type',
        headerName: 'Position Type',
        field: 'positionType',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    SettlementType: {
        colId: 'Settlement Type',
        headerName: 'Settlement Type',
        columnName: 'Settlement Type',
        field: 'settlementSymbol',
        filter: 'agSetColumnFilter',
        sortable: true,
        minWidth: 100,
        flex: 1,
    },
    Actions: {
        colId: 'actions',
        headerName: '',
        width: 40,
        field: 'actions',
        filter: 'actionsFilter',
        resizable: false,
        //cellStyle: { padding: '0' },
        sortable: false,
        suppressAutoSize: true,
        //suppressMovable: true,
        /*cellRenderer: (params: ICellRendererParams<PositionRow>, props: TableProps ) => {
        const [open, setOpen] = useState(false);
        const anchorRef = useRef<HTMLButtonElement | null>(null);

        return <div>
            <IconButton ref={anchorRef} sx={{ borderRadius: 1 }} onClick={() => setOpen(true)}><MoreVert /></IconButton>

            <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" >
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                    <Paper>
                        <MenuList autoFocusItem={open}>
                            <MenuItem onClick={() => { props.onSetPositionValidation(params.data!.dealTicketId!); setOpen(false) }}>Set Validation</MenuItem>
                            <MenuItem onClick={() => { props.onEditPosition(params.data!.dealTicketId!); setOpen(false) }}>Edit</MenuItem>
                            <MenuItem onClick={() => props.onViewAuditTrail(params.data!.positionId!)}>Audit</MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
            </div>
            },*/
    }
};